import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { fmtDate, fmtHours, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLeaveTypes } from "@/context/LeaveTypesContext";
import { PageHeader, RagBadge, StatCard, LeaveTypeBadge, StatusBadge, BookingDetailDialog, useTableSort, SortableTH, Avatar, DateField, Shield, Sensitive, ShieldToggle } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PatternEditor } from "@/pages/PatternEditor";
import { NewRequestDialog } from "@/pages/Requests";
import { SalaryHistory, JobHistory, Timeline, Checklists, Performance, Training, HRCases, Documents, PeopleDetails, MyContactCard } from "@/pages/EmployeeHR";
import { Plus, Trash2, ArrowLeft, FileDown, Mail, Pencil, UserMinus, FileText, FileSpreadsheet, RotateCcw, History } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const REGIONS = [
  { v: "england-and-wales", l: "England & Wales" },
  { v: "scotland", l: "Scotland" },
  { v: "northern-ireland", l: "Northern Ireland" },
];
const Field = ({ label, children }) => (
  <div><Label className="text-xs text-slate-500 mb-1 block">{label}</Label>{children}</div>
);

export default function EmployeeDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { map: ltMap } = useLeaveTypes();
  const isAdmin = user?.role === "admin";
  const [historyFilter, setHistoryFilter] = useState("all");
  const [detailBooking, setDetailBooking] = useState(null);
  const [entOpen, setEntOpen] = useState(false);
  const [remOpen, setRemOpen] = useState(false);
  const [takenOpen, setTakenOpen] = useState(false);
  const [summary, setSummary] = useState(null);
  const [patterns, setPatterns] = useState([]);
  const [allowances, setAllowances] = useState([]);
  const [adjustments, setAdjustments] = useState([]);
  const [year, setYear] = useState(null);
  const [years, setYears] = useState([]);
  const [yearId, setYearId] = useState(null);
  const [emailing, setEmailing] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [managers, setManagers] = useState([]);
  const [emp, setEmp] = useState(null);
  const canEntitlement = emp?._perms?.manage_entitlement;
  const leaveSort = useTableSort(
    (summary?.bookings || []).filter((b) => historyFilter === "all" || b.leave_type === historyFilter),
    { type: (b) => b.leave_type, dates: (b) => b.start_date, hours: (b) => b.total_hours, days: (b) => b.total_days, status: (b) => b.status },
    { key: "dates", dir: "desc" }
  );

  useEffect(() => { if (user && user.role !== "employee") api.get("/employees").then((r) => setManagers(r.data)).catch(() => {}); }, [user]);
  useEffect(() => { api.get("/holiday-years").then((r) => setYears(r.data)).catch(() => {}); }, []);
  useEffect(() => { setYearId(null); }, [id]);

  const load = useCallback(async () => {
    try {
      api.get(`/employees/${id}`).then((r) => setEmp(r.data)).catch(() => {});
      const s = await api.get(`/employees/${id}/summary`, { params: yearId ? { year_id: yearId } : {} });
      setSummary(s.data);
      setYear(s.data.holiday_year);
      const yid = s.data.holiday_year.id;
      if (yearId !== yid) setYearId(yid);
      const [p, a, adj] = await Promise.all([
        api.get(`/employees/${id}/patterns`),
        api.get(`/employees/${id}/allowances`, { params: { year_id: yid } }),
        api.get(`/employees/${id}/adjustments`, { params: { year_id: yid } }),
      ]);
      setPatterns(p.data); setAllowances(a.data); setAdjustments(adj.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, [id, yearId]);
  useEffect(() => { load(); }, [load]);

  if (!summary) return <div className="text-slate-400">Loading…</div>;
  const e = summary.employee;
  const ent = summary.entitlement;

  const exportStatement = (fmt) => window.open(`${API}/reports/employee-statement/${id}?year_id=${year.id}&format=${fmt}`, "_blank");

  const isSelf = user?.employee_id === id;
  const emailStatement = async () => {
    setEmailing(true);
    try {
      const { data } = await api.post(`/reports/employee-statement/${id}/email?year_id=${year.id}`);
      toast.success(data.message);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setEmailing(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Link to="/employees" className="text-sm text-slate-500 hover:text-[#2EA3FD] inline-flex items-center gap-1"><ArrowLeft size={14} />Back to employees</Link>
        <div className="flex items-center gap-2 flex-wrap">
          {years.length > 1 && (
            <Select value={yearId || year?.id || ""} onValueChange={(v) => setYearId(v)}>
              <SelectTrigger className="h-9 w-44 bg-white" data-testid="record-year-select"><SelectValue placeholder="Holiday year" /></SelectTrigger>
              <SelectContent>{years.map((y) => <SelectItem key={y.id} value={y.id}>{y.name}</SelectItem>)}</SelectContent>
            </Select>
          )}
          {managers.length > 1 && user?.role !== "employee" && (
            <Select value={id} onValueChange={(v) => v !== id && nav(`/employees/${v}`)}>
              <SelectTrigger className="h-9 w-64 bg-white" data-testid="employee-switcher"><SelectValue placeholder="Jump to employee…" /></SelectTrigger>
              <SelectContent className="max-h-80">
                {managers.slice().sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`)).map((m) => (
                  <SelectItem key={m.id} value={m.id}>{m.first_name} {m.last_name} ({m.employee_code})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>
      <PageHeader title={`${e.first_name} ${e.last_name}`}
        subtitle={`${e.employee_code} · ${e.department || "—"} · ${summary.holiday_year.name}`}>
        <RagBadge status={summary.rag} />
        <Button variant="outline" size="sm" onClick={() => exportStatement("pdf")} data-testid="export-statement-pdf"><FileDown size={15} className="mr-1" />PDF</Button>
        <Button variant="outline" size="sm" onClick={() => exportStatement("xlsx")} data-testid="export-statement-xlsx"><FileDown size={15} className="mr-1" />Excel</Button>
        <Button variant="outline" size="sm" onClick={emailStatement} disabled={emailing} data-testid="email-statement-btn"><Mail size={15} className="mr-1" />{emailing ? "Sending…" : (isSelf ? "Email to me" : "Email to employee")}</Button>
        {isAdmin && (
          <Dialog open={editOpen} onOpenChange={setEditOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" data-testid="edit-employee-btn"><Pencil size={15} className="mr-1" />Edit details</Button>
            </DialogTrigger>
            <EditEmployeeDialog key={e.id} employee={e} managers={managers} onSaved={() => { setEditOpen(false); load(); }} />
          </Dialog>
        )}
        {isAdmin && (
          <Button variant="outline" size="sm" onClick={() => setHistoryOpen(true)} data-testid="version-history-btn"><History size={15} className="mr-1" />History</Button>
        )}
        <NewRequestDialog onSaved={load} trigger={<Button size="sm" className="bg-[#2EA3FD] hover:bg-[#1B8EE6]"><Plus size={15} className="mr-1" />Book leave</Button>} />
      </PageHeader>

      {isAdmin && <VersionHistoryDialog empId={id} open={historyOpen} onOpenChange={setHistoryOpen} onRestored={load} />}

      {summary.warnings.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800" data-testid="employee-warnings">
          ⚠ {summary.warnings.join(" · ")}
        </div>
      )}

      {e.end_date && user?.role !== "employee" && (
        <div className="flex items-center justify-between gap-3 flex-wrap border-l-4 border-l-rose-500 bg-rose-50/70 border-y border-r border-rose-200/60 rounded-xl p-4" data-testid="leaver-banner">
          <p className="text-sm text-[#0B1A2F]"><span className="font-semibold">Marked as a leaver</span> — leaving date {fmtDate(e.end_date)}. Entitlement is pro-rated to this date.</p>
          <Button variant="outline" size="sm" className="border-rose-300 text-rose-700 hover:bg-rose-100" data-testid="reinstate-btn"
            onClick={async () => {
              if (!window.confirm(`Reinstate ${e.first_name} ${e.last_name} as active? This clears the leaving date.`)) return;
              try { await api.put(`/employees/${id}/reinstate`); toast.success("Employee reinstated as active"); load(); }
              catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
            }}>
            <RotateCcw size={15} className="mr-1" />Reinstate
          </Button>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testid="stat-remaining" label="Confirmed remaining" value={fmtHours(summary.confirmed_remaining)} tone={summary.rag} sub={`${fmtHours(summary.remaining_if_pending)} if pending approved`} onClick={() => setRemOpen(true)} hint="View calculation →" />
        <StatCard testid="stat-total-ent" label="Total entitlement" value={fmtHours(summary.total_entitlement)} sub={`Core ${fmtHours(summary.core_entitlement)}`} onClick={() => setEntOpen(true)} hint="View calculation →" />
        <StatCard testid="stat-bh" label="Bank holidays" value={fmtHours(summary.bank_holiday_hours)} tone="blue" sub="Additional to leave" />
        <StatCard testid="stat-taken" label="Taken / future" value={`${fmtHours(summary.approved_taken)}`} tone="navy" sub={`${fmtHours(summary.approved_future)} future · ${fmtHours(summary.pending)} pending`} onClick={() => setTakenOpen(true)} hint="View breakdown →" />
      </div>

      {isSelf && <MyContactCard employee={e} onSaved={load} />}

      <Tabs defaultValue={new URLSearchParams(window.location.search).get("tab") || "breakdown"}>
        <TabsList className="bg-white border border-slate-200 flex flex-wrap h-auto w-full justify-start gap-1">
          <TabsTrigger value="breakdown" className="whitespace-normal text-center data-[state=active]:bg-[#2EA3FD] data-[state=active]:text-white" data-testid="tab-breakdown">Entitlement</TabsTrigger>
          <TabsTrigger value="patterns" className="whitespace-normal text-center data-[state=active]:bg-[#2EA3FD] data-[state=active]:text-white" data-testid="tab-patterns">Working Patterns</TabsTrigger>
          <TabsTrigger value="adjustments" className="whitespace-normal text-center data-[state=active]:bg-[#2EA3FD] data-[state=active]:text-white" data-testid="tab-adjustments">Adjustments</TabsTrigger>
          <TabsTrigger value="history" className="whitespace-normal text-center data-[state=active]:bg-[#2EA3FD] data-[state=active]:text-white" data-testid="tab-history">Holiday Taken &amp; Booked</TabsTrigger>
          {user?.role !== "employee" && <TabsTrigger value="leaver" className="whitespace-normal text-center data-[state=active]:bg-[#2EA3FD] data-[state=active]:text-white" data-testid="tab-leaver">Leaver Settlement</TabsTrigger>}
          {emp?._perms?.view_salary && <TabsTrigger value="salary" className="whitespace-normal text-center data-[state=active]:bg-[#2EA3FD] data-[state=active]:text-white" data-testid="tab-salary">Salary &amp; Role</TabsTrigger>}
          {emp?._perms?.view_reward && <TabsTrigger value="reward" className="whitespace-normal text-center data-[state=active]:bg-[#2EA3FD] data-[state=active]:text-white" data-testid="tab-reward">Total Reward</TabsTrigger>}
          {(emp?._perms?.is_hr || user?.employee_id === id) && <TabsTrigger value="performance" className="whitespace-normal text-center data-[state=active]:bg-[#2EA3FD] data-[state=active]:text-white" data-testid="tab-performance">Performance</TabsTrigger>}
          {(emp?._perms?.is_hr || user?.employee_id === id) && <TabsTrigger value="training" className="whitespace-normal text-center data-[state=active]:bg-[#2EA3FD] data-[state=active]:text-white" data-testid="tab-training">Training &amp; Certification</TabsTrigger>}
          {emp?._perms?.view_cases && <TabsTrigger value="cases" className="whitespace-normal text-center data-[state=active]:bg-[#2EA3FD] data-[state=active]:text-white" data-testid="tab-cases">HR Cases</TabsTrigger>}
          {(emp?._perms?.is_hr || user?.employee_id === id) && <TabsTrigger value="documents" className="whitespace-normal text-center data-[state=active]:bg-[#2EA3FD] data-[state=active]:text-white" data-testid="tab-documents">Documents</TabsTrigger>}
          {emp?._perms?.is_hr && <TabsTrigger value="onboarding" className="whitespace-normal text-center data-[state=active]:bg-[#2EA3FD] data-[state=active]:text-white" data-testid="tab-onboarding">Checklists</TabsTrigger>}
          {(emp?._perms?.is_hr || user?.employee_id === id) && <TabsTrigger value="timeline" className="whitespace-normal text-center data-[state=active]:bg-[#2EA3FD] data-[state=active]:text-white" data-testid="tab-timeline">Timeline</TabsTrigger>}
        </TabsList>

        {/* Entitlement breakdown */}
        <TabsContent value="breakdown" className="space-y-4">
          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-[#0B1A2F]">Entitlement breakdown</h3>
              <span className="text-xs text-slate-500">Proration: {ent.proration_method} · Rounding: {ent.rounding_method}</span>
            </div>
            <div className="overflow-x-auto cass-scroll">
              <Table>
                <TableHeader><TableRow className="bg-slate-50">
                  {["Period", "Pattern", "Avg wk hrs", "Allowance", "Full-year", "Proration", "Prorated"].map((h) => <TableHead key={h} className="text-xs uppercase text-slate-600">{h}</TableHead>)}
                </TableRow></TableHeader>
                <TableBody>
                  {ent.periods.map((p, i) => (
                    <TableRow key={i} data-testid="entitlement-breakdown-row">
                      <TableCell className="text-sm">{fmtDate(p.start_date)} – {fmtDate(p.end_date)}</TableCell>
                      <TableCell>{p.pattern_name || <span className="text-amber-600">No pattern</span>}{p.cycle_length === 2 ? " (2-wk)" : ""}</TableCell>
                      <TableCell className="tabular">{p.avg_weekly_hours}h</TableCell>
                      <TableCell className="tabular">{p.allowance_weeks} wks</TableCell>
                      <TableCell className="tabular">{fmtHours(p.full_year_entitlement)}</TableCell>
                      <TableCell className="text-xs text-slate-500">{p.proration_factor_desc}</TableCell>
                      <TableCell className="tabular font-semibold text-[#0B1A2F]">{fmtHours(p.prorated_entitlement)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="mt-4 grid sm:grid-cols-2 gap-3 text-sm">
              <CalcLine label="Core entitlement (rounded)" value={fmtHours(summary.core_entitlement)} bold />
              <CalcLine label="Carry-forward" value={fmtHours(summary.carry_forward)} />
              <CalcLine label="Adjustments" value={fmtHours(summary.adjustments_total)} />
              <CalcLine label="Total entitlement" value={fmtHours(summary.total_entitlement)} bold />
              <CalcLine label="Approved taken" value={`− ${fmtHours(summary.approved_taken)}`} />
              <CalcLine label="Approved future" value={`− ${fmtHours(summary.approved_future)}`} />
              <CalcLine label="Confirmed remaining" value={fmtHours(summary.confirmed_remaining)} bold accent />
              <CalcLine label="Pending (not deducted)" value={fmtHours(summary.pending)} />
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm grid sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
            <div><p className="text-xs text-slate-500">Current pattern</p><p className="font-medium">{summary.current_pattern?.name || "—"}</p></div>
            <div><p className="text-xs text-slate-500">Current cycle week</p><p className="font-medium">{summary.current_cycle_week ? `Week ${summary.current_cycle_week}` : "—"}</p></div>
            <div><p className="text-xs text-slate-500">Avg weekly hours</p><p className="font-medium tabular">{summary.current_avg_weekly_hours ?? "—"}h</p></div>
            <div><p className="text-xs text-slate-500">Combined paid time off</p><p className="font-medium tabular">{fmtHours(summary.combined_paid_time_off)}</p></div>
          </div>
        </TabsContent>

        {/* Patterns */}
        <TabsContent value="patterns" className="space-y-4">
          {canEntitlement && <PatternManager empId={id} employee={e} onChanged={load} />}
          <div className="space-y-3">
            {patterns.map((p) => (
              <div key={p.id} className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid={`pattern-card-${p.id}`}>
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-semibold text-[#0B1A2F]">{p.name} <span className="text-xs font-normal text-slate-400">· {p.cycle_length === 2 ? "Two-week cycle" : "One-week"}</span></p>
                    <p className="text-xs text-slate-500">{fmtDate(p.effective_from)} – {p.effective_to ? fmtDate(p.effective_to) : "ongoing"}{p.reason ? ` · ${p.reason}` : ""}</p>
                  </div>
                  {canEntitlement && <div className="flex gap-1">
                    <EditPatternDialog pattern={p} employee={e} onSaved={load} />
                    <Button variant="ghost" size="sm" className="text-rose-600" data-testid={`delete-pattern-${p.id}`}
                    onClick={async () => { if (window.confirm("Delete pattern?")) { await api.delete(`/patterns/${p.id}`); toast.success("Deleted"); load(); } }}><Trash2 size={15} /></Button>
                  </div>}
                </div>
                <div className="mt-3 grid grid-cols-8 gap-1 text-center text-xs">
                  <div />
                  {DAYS.map((d) => <div key={d} className="font-semibold text-slate-500">{d}</div>)}
                  <div className="text-left text-slate-500 self-center">Wk1</div>
                  {p.week1_hours.map((h, i) => <div key={i} className={`py-1 rounded ${h > 0 ? "bg-[#EFF8FF] text-[#0B1A2F]" : "bg-slate-50 text-slate-300"}`}>{h}</div>)}
                  {p.cycle_length === 2 && <><div className="text-left text-slate-500 self-center">Wk2</div>
                    {(p.week2_hours || []).map((h, i) => <div key={i} className={`py-1 rounded ${h > 0 ? "bg-[#EFF8FF] text-[#0B1A2F]" : "bg-slate-50 text-slate-300"}`}>{h}</div>)}</>}
                </div>
                <div className="mt-3 flex gap-4 text-xs text-slate-500">
                  <span>Week 1: <b className="text-[#0B1A2F]">{p.week1_total}h</b></span>
                  {p.cycle_length === 2 && <><span>Week 2: <b className="text-[#0B1A2F]">{p.week2_total}h</b></span><span>Cycle: <b className="text-[#0B1A2F]">{p.cycle_total}h</b></span></>}
                  <span>Avg weekly: <b className="text-[#0B1A2F]">{p.avg_weekly_hours}h</b></span>
                  <span>Avg days/wk: <b className="text-[#0B1A2F]">{p.avg_working_days}</b></span>
                </div>
              </div>
            ))}
            {patterns.length === 0 && <div className="bg-white border rounded-xl p-8 text-center text-slate-400">No working patterns defined</div>}
          </div>
        </TabsContent>

        {/* Adjustments */}
        <TabsContent value="adjustments" className="space-y-4">
          {canEntitlement && <AdjustmentManager empId={id} yearId={year?.id} allowances={allowances} onChanged={load} />}
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-x-auto cass-scroll">
            <Table>
              <TableHeader><TableRow className="bg-slate-50">{["Type", "Hours", "Reason", "By", "Date", ""].map((h) => <TableHead key={h} className="text-xs uppercase text-slate-600">{h}</TableHead>)}</TableRow></TableHeader>
              <TableBody>
                {adjustments.map((a) => (
                  <TableRow key={a.id} data-testid={`adjustment-row-${a.id}`}>
                    <TableCell className="capitalize">{a.type.replace("_", " ")}</TableCell>
                    <TableCell className={`tabular font-semibold ${a.signed_hours < 0 ? "text-rose-600" : "text-emerald-600"}`}>{a.signed_hours > 0 ? "+" : ""}{a.signed_hours}h</TableCell>
                    <TableCell className="text-sm text-slate-600">{a.reason || "—"}</TableCell>
                    <TableCell className="text-xs">{a.created_by}</TableCell>
                    <TableCell className="text-xs">{fmtDate(a.created_at)}</TableCell>
                    <TableCell>{canEntitlement && <Button variant="ghost" size="sm" className="text-rose-600" onClick={async () => { await api.delete(`/adjustments/${a.id}`); toast.success("Removed"); load(); }}><Trash2 size={14} /></Button>}</TableCell>
                  </TableRow>
                ))}
                {adjustments.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-6">No adjustments</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
          {canEntitlement && (
            <div className="bg-white border rounded-xl p-4">
              <h4 className="text-sm font-semibold text-[#0B1A2F] mb-2">Set contractual allowance</h4>
              <AllowanceManager empId={id} yearId={year?.id} allowances={allowances} onChanged={load} />
            </div>
          )}
        </TabsContent>

        {/* Leave taken — breakdown tiles + click-through detail */}
        <TabsContent value="history" className="space-y-4">
          {(summary.leave_type_breakdown || []).length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              <button
                onClick={() => setHistoryFilter("all")}
                data-testid="leave-tile-all"
                className={`text-left bg-white border rounded-xl p-4 shadow-sm transition-all hover:shadow-md hover:-translate-y-0.5 ${historyFilter === "all" ? "border-[#2EA3FD] ring-2 ring-[#2EA3FD]/20" : "border-slate-200"}`}>
                <p className="text-xs uppercase tracking-wide text-slate-500">All leave</p>
                <p className="text-2xl font-bold text-[#0B1A2F] tabular mt-1">{fmtHours(summary.bookings.filter((b) => b.status === "approved").reduce((s, b) => s + b.total_hours, 0))}</p>
                <p className="text-xs text-slate-400 mt-1">{summary.bookings.length} booking{summary.bookings.length !== 1 ? "s" : ""}</p>
              </button>
              {summary.leave_type_breakdown.map((lt) => {
                const color = ltMap[lt.leave_type]?.color || "#2EA3FD";
                const active = historyFilter === lt.leave_type;
                return (
                  <button
                    key={lt.leave_type}
                    onClick={() => setHistoryFilter(lt.leave_type)}
                    data-testid={`leave-tile-${lt.leave_type}`}
                    className={`text-left bg-white border rounded-xl p-4 shadow-sm transition-all hover:shadow-md hover:-translate-y-0.5 ${active ? "ring-2 ring-offset-1" : "border-slate-200"}`}
                    style={active ? { borderColor: color, "--tw-ring-color": color } : undefined}>
                    <div className="flex items-center gap-1.5">
                      <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: color }} />
                      <p className="text-xs uppercase tracking-wide text-slate-500 truncate">{lt.name}</p>
                    </div>
                    <p className="text-2xl font-bold text-[#0B1A2F] tabular mt-1">{fmtHours(lt.approved_hours)}</p>
                    <p className="text-xs text-slate-400 mt-1">
                      {lt.count} booking{lt.count !== 1 ? "s" : ""}
                      {lt.approved_days ? ` · ${lt.approved_days}d` : ""}
                    </p>
                    {lt.pending_hours > 0 && <p className="text-xs text-amber-600 mt-0.5">{fmtHours(lt.pending_hours)} pending</p>}
                  </button>
                );
              })}
            </div>
          )}

          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-x-auto cass-scroll">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
              <h3 className="font-semibold text-[#0B1A2F] text-sm">
                {historyFilter === "all" ? "All leave detail" : `${ltMap[historyFilter]?.name || historyFilter} detail`}
              </h3>
              {historyFilter !== "all" && <Button variant="ghost" size="sm" className="text-slate-500 h-7" onClick={() => setHistoryFilter("all")} data-testid="clear-leave-filter">Show all</Button>}
            </div>
            <Table>
              <TableHeader><TableRow className="bg-slate-50">
                <SortableTH label="Type" sortId="type" sort={leaveSort.sort} onSort={leaveSort.toggle} />
                <SortableTH label="Dates" sortId="dates" sort={leaveSort.sort} onSort={leaveSort.toggle} />
                <SortableTH label="Hours" sortId="hours" sort={leaveSort.sort} onSort={leaveSort.toggle} />
                <SortableTH label="Days" sortId="days" sort={leaveSort.sort} onSort={leaveSort.toggle} />
                <SortableTH label="Status" sortId="status" sort={leaveSort.sort} onSort={leaveSort.toggle} />
              </TableRow></TableHeader>
              <TableBody>
                {leaveSort.sorted.map((b) => (
                  <TableRow key={b.id} data-testid={`leave-detail-row-${b.id}`} className="cursor-pointer hover:bg-slate-50" onClick={() => setDetailBooking(b)}>
                    <TableCell><LeaveTypeBadge type={b.leave_type} /></TableCell>
                    <TableCell className="text-sm">{fmtDate(b.start_date)}{b.start_date !== b.end_date ? ` – ${fmtDate(b.end_date)}` : ""}</TableCell>
                    <TableCell className="tabular">{fmtHours(b.total_hours)}</TableCell>
                    <TableCell className="tabular">{b.total_days}</TableCell>
                    <TableCell><StatusBadge status={b.status} /></TableCell>
                  </TableRow>
                ))}
                {leaveSort.sorted.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-6">No leave recorded</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {user?.role !== "employee" && (
          <TabsContent value="leaver" className="space-y-4">
            <LeaverSettlementPanel empId={id} employee={e} yearId={year.id} onSaved={load} />
          </TabsContent>
        )}

        {emp?._perms?.view_salary && (
          <TabsContent value="salary" className="space-y-4">
            <SalaryHistory empId={id} />
            <JobHistory empId={id} canManage={user?.role === "admin" || user?.role === "hr_admin"} managers={managers} />
            <PeopleDetails employee={e} />
          </TabsContent>
        )}
        {(emp?._perms?.is_hr || user?.employee_id === id) && (
          <TabsContent value="performance" className="space-y-4">
            <Performance empId={id} canManage={emp?._perms?.is_hr} />
          </TabsContent>
        )}
        {(emp?._perms?.is_hr || user?.employee_id === id) && (
          <TabsContent value="training" className="space-y-4">
            <Training empId={id} canManage={emp?._perms?.is_hr} isSelf={user?.employee_id === id} />
          </TabsContent>
        )}
        {emp?._perms?.view_cases && (
          <TabsContent value="cases" className="space-y-4">
            <HRCases empId={id} canView={emp?._perms?.view_cases} canManage={emp?._perms?.manage_cases} />
          </TabsContent>
        )}
        {(emp?._perms?.is_hr || user?.employee_id === id) && (
          <TabsContent value="documents" className="space-y-4">
            <Documents empId={id} canManage={emp?._perms?.manage_documents} />
          </TabsContent>
        )}
        {emp?._perms?.is_hr && (
          <TabsContent value="onboarding" className="space-y-4">
            <Checklists empId={id} canManage={emp?._perms?.is_hr} />
          </TabsContent>
        )}
        {emp?._perms?.view_reward && (
          <TabsContent value="reward" className="space-y-4">
            <RewardTab empId={id} canManage={emp?._perms?.manage_benefits} />
          </TabsContent>
        )}
        {(emp?._perms?.is_hr || user?.employee_id === id) && (
          <TabsContent value="timeline" className="space-y-4">
            <Timeline empId={id} />
          </TabsContent>
        )}
      </Tabs>
      <BookingDetailDialog booking={detailBooking} open={!!detailBooking} onOpenChange={(o) => !o && setDetailBooking(null)} />
      <EntitlementDialog open={entOpen} onOpenChange={setEntOpen} ent={ent} summary={summary} />
      <RemainingDialog open={remOpen} onOpenChange={setRemOpen} summary={summary} />
      <TakenDialog open={takenOpen} onOpenChange={setTakenOpen} summary={summary} />
    </div>
  );
}

const CalcLine = ({ label, value, bold, accent }) => (  <div className={`flex justify-between px-3 py-2 rounded-lg ${accent ? "bg-[#EFF8FF]" : "bg-slate-50/60"}`}>
    <span className={`${bold ? "font-semibold text-[#0B1A2F]" : "text-slate-600"}`}>{label}</span>
    <span className={`tabular ${bold ? "font-bold text-[#0B1A2F]" : "text-slate-700"} ${accent ? "text-[#2EA3FD]" : ""}`}>{value}</span>
  </div>
);

const gbp0 = (n) => `£${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const RWD_STATUS = {
  draft: { label: "Draft", cls: "bg-slate-100 text-slate-600 border-slate-200" },
  pending_approval: { label: "Pending approval", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  approved: { label: "Approved", cls: "bg-blue-100 text-blue-700 border-blue-200" },
  published: { label: "Published", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
};

function RewardTab({ empId, canManage }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    setErr(null);
    return api.get(`/reward/calculate/${empId}`).then((r) => setD(r.data)).catch((e) => setErr(formatApiError(e.response?.data?.detail) || "Unable to load Total Reward"));
  }, [empId]);
  useEffect(() => { setD(null); load(); }, [load]);

  const act = async (action) => {
    setBusy(true);
    try { await api.post(`/reward/statement/${empId}/${action}`, {}); toast.success(`Statement ${action === "revert" ? "reverted to draft" : action + "ed"}`); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const exp = (fmt) => window.open(`${API}/reward/statement/${empId}/export?format=${fmt}`, "_blank");

  if (err) return <div className="bg-white border border-slate-200 rounded-xl p-6 text-sm text-slate-500" data-testid="reward-tab-error">{err}</div>;
  if (!d) return <div className="bg-white border border-slate-200 rounded-xl p-6 text-sm text-slate-400" data-testid="reward-tab-loading">Loading Total Reward…</div>;
  const t = d.totals;
  const cashOnly = t.cash_package - d.salary.annual;
  const st = d.statement || { status: "draft" };
  const sMeta = RWD_STATUS[st.status] || RWD_STATUS.draft;

  return (
    <Shield className="space-y-4" testid="reward-tab">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-sm text-slate-500">Statement period {fmtDate(d.period.start)} – {fmtDate(d.period.end)} · as at {fmtDate(d.as_at)}</p>
        <div className="flex items-center gap-2">
          <span className={`text-xs font-semibold border rounded-full px-2.5 py-0.5 ${sMeta.cls}`} data-testid="reward-status-badge">{sMeta.label}</span>
          <ShieldToggle testid="reward-reveal-toggle" />
          <Button variant="outline" size="sm" onClick={() => exp("pdf")} data-testid="reward-export-pdf"><FileText size={14} className="mr-1" />PDF</Button>
          <Button variant="outline" size="sm" onClick={() => exp("xlsx")} data-testid="reward-export-xlsx"><FileSpreadsheet size={14} className="mr-1" />Excel</Button>
        </div>
      </div>

      {canManage && (
        <div className="bg-white border border-slate-200 rounded-xl p-3 flex flex-wrap items-center gap-2" data-testid="reward-workflow">
          <span className="text-xs text-slate-400 mr-1">Publication:</span>
          {st.status !== "published" && <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" disabled={busy} onClick={() => act("publish")} data-testid="reward-publish-btn">Publish to employee</Button>}
          {st.status === "published" && <><span className="text-xs text-emerald-700">Visible to the employee{st.published_at ? ` · published ${fmtDate(st.published_at)}` : ""}.</span><Button size="sm" variant="outline" className="text-rose-600" disabled={busy} onClick={() => act("revert")} data-testid="reward-unpublish-btn">Unpublish</Button></>}
        </div>
      )}

      {canManage && <BenefitAssignments empId={empId} onChanged={load} />}

      {d.warnings?.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="reward-tab-warnings">
          <p className="text-sm font-semibold text-amber-800 flex items-center gap-2"><FileText size={14} />Validation warnings — review before publishing</p>
          <ul className="list-disc pl-6 mt-2 space-y-1 text-sm text-amber-800">{d.warnings.map((w, i) => <li key={i} data-testid="reward-warning-item">{w}</li>)}</ul>
        </div>
      )}
      {(!d.warnings || d.warnings.length === 0) && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-sm text-emerald-800" data-testid="reward-tab-clean">✓ No validation issues — this statement is ready to publish.</div>
      )}

      <div className="bg-gradient-to-r from-[#0B1A2F] to-[#0F2C50] text-white rounded-2xl p-5" data-testid="reward-tab-headline">
        <p className="text-xs uppercase tracking-wider text-[#8FD0FF]">Indicative total reward</p>
        <p className="text-3xl font-extrabold mt-1"><Sensitive testid="reward-headline-total">{gbp0(t.indicative_total)}</Sensitive></p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
          {[["Basic salary", d.salary.annual], ["Cash allowances", cashOnly], ["Employer-funded benefits", t.employer_benefits], ["Additional paid leave", t.additional_leave]].map(([l, v]) => (
            <div key={l} className="bg-white/10 rounded-lg px-3 py-2"><p className="text-[11px] text-[#B9DCFF]">{l}</p><p className="text-base font-bold"><Sensitive>{gbp0(v)}</Sensitive></p></div>
          ))}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <Table>
          <TableHeader><TableRow className="bg-slate-50">{["Component", "Category", "Annual value"].map((h) => <TableHead key={h} className="text-xs uppercase text-slate-600">{h}</TableHead>)}</TableRow></TableHeader>
          <TableBody>
            <TableRow data-testid="reward-line-salary"><TableCell className="text-sm font-medium">Basic annual salary{d.salary.effective_from && <span className="block text-xs text-slate-400">Effective {fmtDate(d.salary.effective_from)}</span>}</TableCell><TableCell className="text-xs text-slate-500">Salary</TableCell><TableCell className="tabular text-sm font-semibold text-[#0B1A2F]"><Sensitive>{gbp0(d.salary.annual)}</Sensitive></TableCell></TableRow>
            {d.cash_allowances.map((c, i) => <TableRow key={`c${i}`} data-testid="reward-line-cash"><TableCell className="text-sm">{c.name}{c.taxable && <span className="block text-xs text-amber-600">Taxable</span>}</TableCell><TableCell className="text-xs text-slate-500">Cash allowance</TableCell><TableCell className="tabular text-sm"><Sensitive>{gbp0(c.annual_value)}</Sensitive></TableCell></TableRow>)}
            {d.employer_benefits.map((b, i) => <TableRow key={`b${i}`} data-testid="reward-line-benefit"><TableCell className="text-sm">{b.name}{b.taxable && <span className="block text-xs text-amber-600">Taxable</span>}</TableCell><TableCell className="text-xs text-slate-500">Employer-funded benefit</TableCell><TableCell className="tabular text-sm"><Sensitive>{gbp0(b.annual_value)}</Sensitive></TableCell></TableRow>)}
            {d.additional_leave.map((a, i) => <TableRow key={`l${i}`} data-testid="reward-line-leave"><TableCell className="text-sm">{a.name}{a.dates?.length > 0 && <span className="block text-xs text-slate-400">{a.dates.map((x) => `${x.label || "Shutdown"} ${fmtDate(x.date)} (${x.hours}h)`).join(", ")}</span>}{(!a.dates || a.dates.length === 0) && <span className="block text-xs text-slate-400">Not applicable this period</span>}</TableCell><TableCell className="text-xs text-slate-500">Additional leave (illustrative)</TableCell><TableCell className="tabular text-sm"><Sensitive>{gbp0(a.annual_value)}</Sensitive></TableCell></TableRow>)}
            <TableRow className="bg-[#EFF8FF]"><TableCell className="text-sm font-bold text-[#0B1A2F]">Indicative total reward</TableCell><TableCell /><TableCell className="tabular text-sm font-bold text-[#2EA3FD]"><Sensitive>{gbp0(t.indicative_total)}</Sensitive></TableCell></TableRow>
          </TableBody>
        </Table>
      </div>

      <p className="text-xs text-slate-400">Additional paid-leave values are illustrative and are not additional cash payments. This figure excludes tax, National Insurance and payroll deductions.</p>
    </Shield>
  );
}


const BEN_CAT = { cash_allowance: "Cash allowance", employer_benefit: "Employer-funded benefit", additional_leave: "Additional leave", display_only: "Display only" };

function BenefitAssignments({ empId, onChanged }) {
  const [rows, setRows] = useState(null);
  const [cat, setCat] = useState([]);
  const [dlg, setDlg] = useState(null);
  const load = useCallback(() => {
    Promise.all([api.get(`/employees/${empId}/benefits`), api.get(`/reward/catalogue`)])
      .then(([b, c]) => { setRows(b.data); setCat(c.data); }).catch(() => setRows([]));
  }, [empId]);
  useEffect(() => { load(); }, [load]);
  const del = async (bid) => {
    if (!window.confirm("Remove this benefit from the employee?")) return;
    try { await api.delete(`/employee-benefits/${bid}`); toast.success("Benefit removed"); load(); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const save = async (form, mode, bid) => {
    try {
      const body = { catalogue_id: form.catalogue_id, annual_value: form.annual_value === "" ? null : Number(form.annual_value),
        start_date: form.start_date || null, end_date: form.end_date || null, include_in_total: form.include_in_total, admin_note: form.admin_note || null };
      if (mode === "edit") await api.put(`/employee-benefits/${bid}`, body); else await api.post(`/employees/${empId}/benefits`, body);
      toast.success("Benefit saved"); setDlg(null); load(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  if (rows === null) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="benefit-assignments">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-semibold text-[#0B1A2F] text-sm">Assigned benefits</h3>
        <Button size="sm" variant="outline" onClick={() => setDlg({ mode: "add", data: { catalogue_id: "", annual_value: "", start_date: "", end_date: "", include_in_total: true, admin_note: "" } })} data-testid="benefit-add-btn"><Plus size={14} className="mr-1" />Add benefit</Button>
      </div>
      {rows.length === 0 ? <p className="text-xs text-slate-400">No benefits assigned to this employee yet.</p> : (
        <Table>
          <TableHeader><TableRow className="bg-slate-50">{["Benefit", "Category", "Annual value", "Dates", "In total", ""].map((h) => <TableHead key={h} className="text-xs uppercase text-slate-600">{h}</TableHead>)}</TableRow></TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} data-testid={`benefit-row-${r.id}`}>
                <TableCell className="text-sm">{r.catalogue?.name || "—"}</TableCell>
                <TableCell className="text-xs text-slate-500">{BEN_CAT[r.catalogue?.category] || r.catalogue?.category}</TableCell>
                <TableCell className="tabular text-sm">{(r.value_override ?? r.annual_value) != null ? <Sensitive>{`£${Number(r.value_override ?? r.annual_value).toLocaleString()}`}</Sensitive> : <span className="text-slate-400">auto</span>}</TableCell>
                <TableCell className="text-xs text-slate-500">{r.start_date ? fmtDate(r.start_date) : "—"}{r.end_date ? ` – ${fmtDate(r.end_date)}` : ""}</TableCell>
                <TableCell className="text-xs">{r.include_in_total !== false ? "Yes" : "No"}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button size="sm" variant="ghost" className="h-7" onClick={() => setDlg({ mode: "edit", bid: r.id, data: { catalogue_id: r.catalogue_id, annual_value: (r.value_override ?? r.annual_value) ?? "", start_date: r.start_date || "", end_date: r.end_date || "", include_in_total: r.include_in_total !== false, admin_note: r.admin_note || "" } })} data-testid={`benefit-edit-${r.id}`}><Pencil size={13} /></Button>
                  <Button size="sm" variant="ghost" className="h-7 text-rose-500" onClick={() => del(r.id)} data-testid={`benefit-delete-${r.id}`}><Trash2 size={13} /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {dlg && <BenefitDialog cat={cat} dlg={dlg} onClose={() => setDlg(null)} onSave={save} />}
    </div>
  );
}

function BenefitDialog({ cat, dlg, onClose, onSave }) {
  const [f, setF] = useState(dlg.data);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="benefit-dialog">
        <DialogHeader><DialogTitle>{dlg.mode === "edit" ? "Edit benefit" : "Add benefit"}</DialogTitle><DialogDescription>Assign a benefit from the catalogue to this employee and set its value for the reward statement.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs text-slate-500">Benefit</Label>
            <Select value={f.catalogue_id} onValueChange={(v) => setF({ ...f, catalogue_id: v })}>
              <SelectTrigger data-testid="benefit-catalogue-select"><SelectValue placeholder="Choose a benefit" /></SelectTrigger>
              <SelectContent>{cat.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label className="text-xs text-slate-500">Annual value (£) — leave blank to use the benefit default</Label><Input type="number" value={f.annual_value} onChange={(e) => setF({ ...f, annual_value: e.target.value })} data-testid="benefit-value-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs text-slate-500">Start date</Label><Input type="date" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} data-testid="benefit-start-input" /></div>
            <div><Label className="text-xs text-slate-500">End date</Label><Input type="date" value={f.end_date} onChange={(e) => setF({ ...f, end_date: e.target.value })} data-testid="benefit-end-input" /></div>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={f.include_in_total} onChange={(e) => setF({ ...f, include_in_total: e.target.checked })} data-testid="benefit-include-input" />Include in indicative total reward</label>
          <div><Label className="text-xs text-slate-500">Admin note (not shown to the employee)</Label><Input value={f.admin_note} onChange={(e) => setF({ ...f, admin_note: e.target.value })} data-testid="benefit-note-input" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-[#2EA3FD] hover:bg-[#1B8EE6]" disabled={!f.catalogue_id} onClick={() => onSave(f, dlg.mode, dlg.bid)} data-testid="benefit-save-btn">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const SettleRow = ({ label, value, strong }) => (
  <div className={`flex items-center justify-between py-2 border-b border-slate-100 ${strong ? "font-semibold text-[#0B1A2F]" : "text-slate-600"}`}>
    <span className="text-sm">{label}</span><span className="tabular text-sm">{value}</span>
  </div>
);

function LeaverSettlementPanel({ empId, employee, yearId, onSaved }) {
  const isLeaver = !!employee.end_date;
  const [leaving, setLeaving] = useState(employee.end_date || "");
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [reinstating, setReinstating] = useState(false);
  const [result, setResult] = useState(employee.leaver_settlement || null);

  const calculate = async () => {
    if (!leaving) { toast.error("Enter a leaving date"); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/reports/leaver-settlement/${empId}`, { params: { leaving_date: leaving, year_id: yearId, format: "json" } });
      setResult(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  const confirmLeaver = async () => {
    if (!leaving) { toast.error("Enter a leaving date"); return; }
    if (!window.confirm(`Mark ${employee.first_name} ${employee.last_name} as a leaver from ${leaving}? Their entitlement will be pro-rated to this date.`)) return;
    setConfirming(true);
    try {
      await api.put(`/employees/${empId}/mark-leaver`, { leaving_date: leaving, year_id: yearId });
      toast.success("Employee marked as leaver");
      onSaved && onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setConfirming(false); }
  };
  const reinstate = async () => {
    if (!window.confirm(`Reinstate ${employee.first_name} ${employee.last_name}? Their leaving date will be cleared and entitlement will no longer be pro-rated.`)) return;
    setReinstating(true);
    try {
      await api.put(`/employees/${empId}/reinstate`);
      toast.success("Employee reinstated");
      onSaved && onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setReinstating(false); }
  };
  const exportReport = (fmt) => window.open(`${API}/reports/leaver-settlement/${empId}?leaving_date=${leaving}&year_id=${yearId}&format=${fmt}`, "_blank");

  const os = result?.outcome === "clawback" ? { color: "#e11d48", label: "Overtaken — to claw back" }
    : result?.outcome === "owed" ? { color: "#059669", label: "Owed — to be paid" }
    : { color: "#0B1A2F", label: "Settled — nothing due" };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-4" data-testid="leaver-settlement-panel">
      <div className="flex items-start gap-3">
        <span className="h-11 w-11 rounded-full bg-[#0B1A2F]/5 grid place-items-center text-[#0B1A2F] shrink-0"><UserMinus size={20} /></span>
        <div className="min-w-0">
          <h3 className="font-semibold text-[#0B1A2F]">Leaver settlement</h3>
          <p className="text-xs text-slate-500">Work out accrued-to-date holiday in hours and what's owed to pay out or claw back if this employee leaves (exact calendar-day pro-rata).</p>
        </div>
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[180px]"><Label className="text-xs text-slate-500">Leaving date</Label><Input type="date" value={leaving} onChange={(ev) => setLeaving(ev.target.value)} disabled={isLeaver} data-testid="leaver-tile-date-input" /></div>
        <Button onClick={calculate} disabled={loading || isLeaver} className="bg-[#0B1A2F] hover:bg-[#162B48]" data-testid="leaver-tile-calc-btn">{loading ? "Calculating…" : "Calculate"}</Button>
      </div>
      {isLeaver && <p className="text-xs text-slate-500 -mt-1">Confirmed leaver settlement — frozen at {result?.confirmed_at ? fmtDate(result.confirmed_at) : "confirmation"}. Reinstate to change the leaving date.</p>}
      {result && (
        <>
          <div className="rounded-xl p-4 text-center" style={{ backgroundColor: os.color + "12" }} data-testid="settlement-headline">
            <p className="text-xs uppercase tracking-wider" style={{ color: os.color }}>{os.label}</p>
            <p className="text-4xl font-extrabold mt-1" style={{ color: os.color }}>{Math.abs(result.settlement_hours)}h</p>
            <p className="text-xs text-slate-500 mt-1">Leaving {fmtDate(result.leaving_date)} · {result.days_employed}/{result.days_in_year} days ({(result.proration_factor * 100).toFixed(1)}%)</p>
          </div>
          <div>
            <SettleRow label="Core accrued to leaving date" value={`${result.core_accrued}h`} />
            {result.carry_forward !== 0 && <SettleRow label="Carry-forward" value={`${result.carry_forward}h`} />}
            {result.adjustments_total !== 0 && <SettleRow label="Adjustments" value={`${result.adjustments_total}h`} />}
            <SettleRow label="Total accrued entitlement" value={`${result.accrued_total}h`} strong />
            <SettleRow label={`Leave taken (${result.taken_days} days)`} value={`−${result.taken}h`} />
            <div className="flex items-center justify-between pt-3 font-bold" style={{ color: os.color }} data-testid="row-settlement"><span>{os.label}</span><span className="tabular">{result.settlement_hours}h</span></div>
          </div>
          {result.taken_breakdown.length > 0 && (
            <div className="text-xs text-slate-500">
              <p className="font-medium text-slate-600 mb-1">Leave taken by type</p>
              {result.taken_breakdown.map((t) => <div key={t.leave_type} className="flex justify-between py-0.5"><span>{t.name} ({t.count})</span><span className="tabular">{t.hours}h · {t.days}d</span></div>)}
            </div>
          )}
          {result.warnings?.length > 0 && <p className="text-xs text-amber-600">{result.warnings.join("; ")}</p>}
          <div className="flex gap-2 pt-2 border-t">
            <Button variant="outline" size="sm" onClick={() => exportReport("pdf")} data-testid="export-settlement-pdf-btn"><FileText size={15} className="mr-1" />PDF</Button>
            <Button variant="outline" size="sm" onClick={() => exportReport("xlsx")} data-testid="export-settlement-xlsx-btn"><FileSpreadsheet size={15} className="mr-1" />XLSX</Button>
            <Button variant="outline" size="sm" onClick={() => exportReport("csv")} data-testid="export-settlement-csv-btn"><FileDown size={15} className="mr-1" />CSV</Button>
          </div>
        </>
      )}
      <div className="flex items-center justify-between gap-2 border-t pt-3">
        <Button onClick={reinstate} disabled={reinstating || !employee.end_date} className="bg-slate-100 text-slate-700 hover:bg-slate-200 border border-slate-200" data-testid="leaver-reinstate-btn">{reinstating ? "Reinstating…" : "Reinstate"}</Button>
        <Button onClick={confirmLeaver} disabled={confirming || !leaving || isLeaver} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="leaver-confirm-btn">{confirming ? "Saving…" : isLeaver ? "Marked as leaver" : "Confirm & mark as leaver"}</Button>
      </div>
    </div>
  );
}

function EntitlementDialog({ open, onOpenChange, ent, summary }) {
  const multi = (ent?.periods || []).length > 1;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="entitlement-dialog">
        <DialogHeader>
          <DialogTitle>How the annual entitlement is calculated</DialogTitle>
          <DialogDescription>
            {summary.holiday_year.name} · Proration: {ent.proration_method} · Rounding: {ent.rounding_method}
            {multi ? " · Split into periods because the working pattern or allowance changed mid-year." : ""}
          </DialogDescription>
        </DialogHeader>
        <div className="border border-slate-200 rounded-lg overflow-x-auto cass-scroll">
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              {["Period", "Pattern", "Avg wk hrs", "Allowance", "Full-year", "Proration", "Prorated"].map((h) => <TableHead key={h} className="text-xs uppercase text-slate-600">{h}</TableHead>)}
            </TableRow></TableHeader>
            <TableBody>
              {ent.periods.map((p, i) => (
                <TableRow key={i} data-testid={`ent-dialog-period-${i}`}>
                  <TableCell className="text-sm whitespace-nowrap">{fmtDate(p.start_date)} – {fmtDate(p.end_date)}</TableCell>
                  <TableCell className="text-sm">{p.pattern_name || <span className="text-amber-600">No pattern</span>}{p.cycle_length === 2 ? " (2-wk)" : ""}</TableCell>
                  <TableCell className="tabular">{p.avg_weekly_hours}h</TableCell>
                  <TableCell className="tabular">{p.allowance_weeks} wks</TableCell>
                  <TableCell className="tabular">{fmtHours(p.full_year_entitlement)}</TableCell>
                  <TableCell className="text-xs text-slate-500">{p.proration_factor_desc}</TableCell>
                  <TableCell className="tabular font-semibold text-[#0B1A2F]">{fmtHours(p.prorated_entitlement)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <p className="text-xs text-slate-500 -mt-1">Each period = avg weekly hours × allowance weeks (full-year), then prorated for the portion of the year worked. Periods are summed and rounded to give the core entitlement.</p>
        <div className="grid sm:grid-cols-2 gap-3 text-sm">
          <CalcLine label="Sum of prorated periods" value={fmtHours(ent.raw_total)} />
          <CalcLine label="Core entitlement (rounded)" value={fmtHours(summary.core_entitlement)} bold />
          <CalcLine label="Carry-forward" value={fmtHours(summary.carry_forward)} />
          <CalcLine label="Adjustments" value={fmtHours(summary.adjustments_total)} />
          <CalcLine label="Total entitlement" value={fmtHours(summary.total_entitlement)} bold accent />
        </div>
      </DialogContent>
    </Dialog>
  );
}


function TakenDialog({ open, onOpenChange, summary }) {
  const breakdown = summary.leave_type_breakdown || [];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="taken-dialog">
        <DialogHeader>
          <DialogTitle>Taken &amp; future leave</DialogTitle>
          <DialogDescription>{summary.holiday_year.name} · approved leave already used, booked ahead, and any pending requests.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 text-sm">
          <CalcLine label="Approved taken (past)" value={fmtHours(summary.approved_taken)} bold />
          <CalcLine label="Approved future (booked ahead)" value={fmtHours(summary.approved_future)} />
          <CalcLine label="Pending (awaiting approval)" value={fmtHours(summary.pending)} />
        </div>
        {breakdown.length > 0 && (
          <div className="border border-slate-200 rounded-lg overflow-hidden mt-1">
            <Table>
              <TableHeader><TableRow className="bg-slate-50">{["Leave type", "Approved", "Pending", "Bookings"].map((h) => <TableHead key={h} className="text-xs uppercase text-slate-600">{h}</TableHead>)}</TableRow></TableHeader>
              <TableBody>
                {breakdown.map((lt) => (
                  <TableRow key={lt.leave_type} data-testid={`taken-type-${lt.leave_type}`}>
                    <TableCell><LeaveTypeBadge type={lt.leave_type} /></TableCell>
                    <TableCell className="tabular">{fmtHours(lt.approved_hours)}</TableCell>
                    <TableCell className="tabular">{fmtHours(lt.pending_hours)}</TableCell>
                    <TableCell className="tabular">{lt.count}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        <p className="text-xs text-slate-500">Only approved leave reduces the balance; pending is shown for information.</p>
      </DialogContent>
    </Dialog>
  );
}

function RemainingDialog({ open, onOpenChange, summary }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="remaining-dialog">
        <DialogHeader>
          <DialogTitle>How the remaining balance is calculated</DialogTitle>
          <DialogDescription>{summary.holiday_year.name} · starting from total entitlement, minus leave already used or booked.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 text-sm">
          <CalcLine label="Total entitlement" value={fmtHours(summary.total_entitlement)} bold />
          <CalcLine label="Approved taken (past)" value={`− ${fmtHours(summary.approved_taken)}`} />
          <CalcLine label="Approved future (booked)" value={`− ${fmtHours(summary.approved_future)}`} />
          <CalcLine label="Confirmed remaining" value={fmtHours(summary.confirmed_remaining)} bold accent />
          <div className="pt-1 border-t border-slate-100" />
          <CalcLine label="Pending (awaiting approval, not deducted)" value={fmtHours(summary.pending)} />
          <CalcLine label="Remaining if pending approved" value={fmtHours(summary.remaining_if_pending)} />
        </div>
        <p className="text-xs text-slate-500">Only approved leave reduces the confirmed balance. Pending requests are shown separately so you can see the effect if they are approved. Bank holidays are additional and do not reduce this balance.</p>
      </DialogContent>
    </Dialog>
  );
}


function PatternManager({ empId, employee, onChanged }) {
  const [open, setOpen] = useState(false);
  return (    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="bg-[#2EA3FD] hover:bg-[#1B8EE6]" data-testid="add-pattern-btn"><Plus size={16} className="mr-2" />Add working pattern</Button></DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle>New working pattern</DialogTitle></DialogHeader>
        <PatternEditor empId={empId} employee={employee} onSaved={() => { setOpen(false); onChanged(); }} />
      </DialogContent>
    </Dialog>
  );
}

function EditPatternDialog({ pattern, employee, onSaved }) {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="text-slate-500 hover:text-[#2EA3FD]" data-testid={`edit-pattern-${pattern.id}`}><Pencil size={15} /></Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Edit working pattern</DialogTitle></DialogHeader>
        <PatternEditor empId={employee.id} employee={employee} existing={pattern} onSaved={() => { setOpen(false); onSaved(); }} />
      </DialogContent>
    </Dialog>
  );
}

function AllowanceManager({ empId, yearId, allowances, onChanged }) {
  const [f, setF] = useState({ allowance_weeks: 5, effective_from: "", reason: "" });
  const save = async () => {
    try {
      await api.post(`/employees/${empId}/allowances`, { holiday_year_id: yearId, allowance_weeks: Number(f.allowance_weeks), effective_from: f.effective_from, reason: f.reason });
      toast.success("Allowance set"); setF({ allowance_weeks: 5, effective_from: "", reason: "" }); onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const rows = (allowances || []).filter((a) => !yearId || a.holiday_year_id === yearId);
  return (
    <div className="space-y-4">
      {rows.length > 0 && (
        <div className="border border-slate-200 rounded-lg overflow-hidden">
          <Table>
            <TableHeader><TableRow className="bg-slate-50">{["Effective from", "Allowance", "Reason", ""].map((h) => <TableHead key={h} className="text-xs uppercase text-slate-600">{h}</TableHead>)}</TableRow></TableHeader>
            <TableBody>
              {rows.map((a) => (
                <TableRow key={a.id} data-testid={`allowance-row-${a.id}`}>
                  <TableCell className="text-sm">{fmtDate(a.effective_from)}</TableCell>
                  <TableCell className="tabular font-semibold text-[#0B1A2F]">{a.allowance_weeks} wks</TableCell>
                  <TableCell className="text-sm text-slate-600">{a.reason || "—"}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-1 justify-end">
                      <EditAllowanceDialog allowance={a} onSaved={onChanged} />
                      <Button variant="ghost" size="sm" className="text-rose-600" data-testid={`delete-allowance-${a.id}`}
                        onClick={async () => { if (window.confirm("Delete this allowance entry?")) { await api.delete(`/allowances/${a.id}`); toast.success("Removed"); onChanged(); } }}><Trash2 size={14} /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <div className="grid sm:grid-cols-4 gap-3 items-end">
        <div><Label className="text-xs text-slate-500">Allowance (weeks)</Label><Input type="number" step="0.5" value={f.allowance_weeks} onChange={(e) => setF({ ...f, allowance_weeks: e.target.value })} data-testid="allowance-weeks-input" /></div>
        <div><Label className="text-xs text-slate-500">Effective from</Label><Input type="date" value={f.effective_from} onChange={(e) => setF({ ...f, effective_from: e.target.value })} data-testid="allowance-date-input" /></div>
        <div><Label className="text-xs text-slate-500">Reason</Label><Input value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="allowance-reason-input" /></div>
        <Button onClick={save} className="bg-[#0B1A2F]" data-testid="allowance-save-btn">Add allowance</Button>
      </div>
    </div>
  );
}

function EditAllowanceDialog({ allowance, onSaved }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({
    allowance_weeks: allowance.allowance_weeks, effective_from: allowance.effective_from || "", reason: allowance.reason || "",
  });
  const save = async () => {
    try {
      await api.put(`/allowances/${allowance.id}`, {
        holiday_year_id: allowance.holiday_year_id, allowance_weeks: Number(f.allowance_weeks),
        effective_from: f.effective_from, reason: f.reason,
      });
      toast.success("Allowance updated"); setOpen(false); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="text-slate-500 hover:text-[#2EA3FD]" data-testid={`edit-allowance-${allowance.id}`}><Pencil size={14} /></Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Edit allowance</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs text-slate-500">Allowance (weeks)</Label><Input type="number" step="0.5" value={f.allowance_weeks} onChange={(e) => setF({ ...f, allowance_weeks: e.target.value })} data-testid="edit-allowance-weeks" /></div>
          <div><Label className="text-xs text-slate-500">Effective from</Label><Input type="date" value={f.effective_from} onChange={(e) => setF({ ...f, effective_from: e.target.value })} data-testid="edit-allowance-date" /></div>
          <div><Label className="text-xs text-slate-500">Reason</Label><Input value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button onClick={save} className="bg-[#2EA3FD] hover:bg-[#1B8EE6]" data-testid="edit-allowance-save">Save changes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AdjustmentManager({ empId, yearId, onChanged }) {
  const [f, setF] = useState({ hours: "", direction: "add", type: "carry_forward", reason: "" });
  const save = async () => {
    try {
      await api.post(`/employees/${empId}/adjustments`, { holiday_year_id: yearId, hours: Number(f.hours), direction: f.direction, type: f.type, reason: f.reason });
      toast.success("Adjustment added"); setF({ hours: "", direction: "add", type: "carry_forward", reason: "" }); onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="bg-white border rounded-xl p-4 grid sm:grid-cols-5 gap-3 items-end">
      <div>
        <Label className="text-xs text-slate-500">Type</Label>
        <Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}>
          <SelectTrigger data-testid="adjustment-type-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {[["carry_forward", "Carried-forward"], ["additional", "Additional leave"], ["purchased", "Purchased"], ["sold", "Sold"], ["toil", "TOIL"], ["correction", "Correction"], ["other", "Other"]].map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div><Label className="text-xs text-slate-500">Direction</Label>
        <Select value={f.direction} onValueChange={(v) => setF({ ...f, direction: v })}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="add">Add (+)</SelectItem><SelectItem value="deduct">Deduct (−)</SelectItem></SelectContent>
        </Select>
      </div>
      <div><Label className="text-xs text-slate-500">Hours</Label><Input type="number" step="0.25" value={f.hours} onChange={(e) => setF({ ...f, hours: e.target.value })} data-testid="adjustment-hours-input" /></div>
      <div><Label className="text-xs text-slate-500">Reason</Label><Input value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} /></div>
      <Button onClick={save} className="bg-[#0B1A2F]" data-testid="adjustment-save-btn">Add</Button>
    </div>
  );
}


function VersionHistoryDialog({ empId, open, onOpenChange, onRestored }) {
  const [versions, setVersions] = useState(null);
  const [busy, setBusy] = useState(null);
  useEffect(() => {
    if (open) { setVersions(null); api.get(`/employees/${empId}/versions`).then((r) => setVersions(r.data)).catch(() => setVersions([])); }
  }, [open, empId]);
  const restore = async (v) => {
    if (!window.confirm(`Restore this record to the version saved before the edit on ${new Date(v.timestamp).toLocaleString("en-GB")} (${v.name || "unnamed"})? This overwrites the current details.`)) return;
    setBusy(v.id);
    try {
      const { data } = await api.post(`/employees/${empId}/restore/${v.id}`);
      toast.success(data.message || "Version restored");
      onRestored(); onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="version-history-dialog">
        <DialogHeader>
          <DialogTitle>Version history</DialogTitle>
          <DialogDescription>Each entry is a snapshot of this employee taken <strong>before</strong> an edit. Restore one to undo an accidental change.</DialogDescription>
        </DialogHeader>
        {versions === null ? (
          <p className="text-sm text-slate-400" data-testid="versions-loading">Loading versions…</p>
        ) : versions.length === 0 ? (
          <p className="text-sm text-slate-400" data-testid="versions-empty">No previous versions recorded for this employee.</p>
        ) : (
          <div className="space-y-2">
            {versions.map((v) => (
              <div key={v.id} className="flex items-center justify-between gap-3 border border-slate-200 rounded-lg p-3" data-testid={`version-${v.id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-[#0B1A2F] truncate">{v.name || "—"}{v.employee_code ? ` · ${v.employee_code}` : ""}</p>
                  <p className="text-xs text-slate-400 truncate">{v.email || ""}</p>
                  <p className="text-xs text-slate-400">Before edit on {new Date(v.timestamp).toLocaleString("en-GB")} by {v.user_name || "system"}</p>
                </div>
                <Button size="sm" variant="outline" disabled={busy === v.id} onClick={() => restore(v)} data-testid={`restore-version-${v.id}`}><RotateCcw size={13} className="mr-1" />{busy === v.id ? "Restoring…" : "Restore"}</Button>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


function EditEmployeeDialog({ employee, managers, onSaved }) {
  const [f, setF] = useState({
    employee_code: employee.employee_code || "",
    first_name: employee.first_name || "",
    last_name: employee.last_name || "",
    email: employee.email || "",
    department: employee.department || "",
    manager_id: employee.manager_id || "none",
    start_date: employee.start_date || "",
    end_date: employee.end_date || "",
    probation_months: employee.probation_months ?? 3,
    date_of_birth: employee.date_of_birth || "",
    photo_url: employee.photo_url || "",
    region: employee.region || "england-and-wales",
    bank_holiday_treatment: employee.bank_holiday_treatment || "additional",
    active: employee.active !== false,
    preferred_name: employee.preferred_name || "",
    phone: employee.phone || "",
    work_phone: employee.work_phone || "",
    address: employee.address || "",
    ni_number: employee.ni_number || "",
    emergency_contact: employee.emergency_contact || "",
    emergency_relationship: employee.emergency_relationship || "",
    right_to_work: employee.right_to_work || "",
    employment_type: employee.employment_type || "full_time",
    notice_period: employee.notice_period || "",
    benefits: employee.benefits || "",
    equipment: employee.equipment || "",
    medical_notes: employee.medical_notes || "",
  });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const onPhoto = (file) => {
    if (!file) return;
    const img = new Image();
    const reader = new FileReader();
    reader.onload = () => {
      img.onload = () => {
        const size = 256;
        const c = document.createElement("canvas");
        c.width = size; c.height = size;
        const ctx = c.getContext("2d");
        const scale = Math.max(size / img.width, size / img.height);
        const w = img.width * scale, h = img.height * scale;
        ctx.drawImage(img, (size - w) / 2, (size - h) / 2, w, h);
        set("photo_url", c.toDataURL("image/jpeg", 0.8));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };
  const save = async () => {
    try {
      const payload = { ...f, manager_id: f.manager_id === "none" ? null : f.manager_id, end_date: f.end_date || null, date_of_birth: f.date_of_birth || null, photo_url: f.photo_url || null };
      const { data } = await api.put(`/employees/${employee.id}`, payload);
      const n = data?.recalculated_bookings?.length || 0;
      toast.success(n > 0 ? `Employee updated · ${n} booking(s) recalculated` : "Employee updated");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const otherManagers = managers.filter((m) => m.id !== employee.id);

  return (
    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
      <DialogHeader><DialogTitle>Edit employee details</DialogTitle></DialogHeader>
      <div className="flex items-center gap-3 rounded-lg border border-[#2EA3FD]/30 bg-[#2EA3FD]/5 px-3 py-2" data-testid="edit-emp-banner">
        <Avatar name={`${employee.first_name} ${employee.last_name}`} photo={employee.photo_url} size={32} />
        <p className="text-sm text-[#0B1A2F]">
          <span className="text-slate-500">Editing:</span>{" "}
          <span className="font-semibold">{employee.first_name} {employee.last_name}</span>
          {employee.employee_code ? <span className="text-slate-400"> · {employee.employee_code}</span> : null}
        </p>
      </div>
      <div className="flex items-center gap-4 mb-2">
        <div className="h-16 w-16 rounded-full overflow-hidden bg-[#0B1A2F] text-white flex items-center justify-center text-lg font-bold shrink-0">
          {f.photo_url ? <img src={f.photo_url} alt="avatar" className="h-full w-full object-cover" data-testid="emp-photo-preview" /> : `${(f.first_name[0] || "").toUpperCase()}${(f.last_name[0] || "").toUpperCase()}`}
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-slate-500">Profile picture</Label>
          <div className="flex items-center gap-2">
            <Input type="file" accept="image/*" className="text-xs" onChange={(e) => onPhoto(e.target.files?.[0])} data-testid="edit-emp-photo" />
            {f.photo_url && <Button type="button" variant="ghost" size="sm" className="text-rose-600 h-8" onClick={() => set("photo_url", "")} data-testid="edit-emp-photo-remove">Remove</Button>}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Employee code"><Input data-testid="edit-emp-code" value={f.employee_code} onChange={(e) => set("employee_code", e.target.value)} /></Field>
        <Field label="Department"><Input value={f.department} onChange={(e) => set("department", e.target.value)} /></Field>
        <Field label="First name"><Input value={f.first_name} onChange={(e) => set("first_name", e.target.value)} /></Field>
        <Field label="Last name"><Input value={f.last_name} onChange={(e) => set("last_name", e.target.value)} /></Field>
        <Field label="Work email"><Input type="email" value={f.email} onChange={(e) => set("email", e.target.value)} /></Field>
        <Field label="Manager">
          <Select value={f.manager_id} onValueChange={(v) => set("manager_id", v)}>
            <SelectTrigger data-testid="edit-emp-manager"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No manager</SelectItem>
              {otherManagers.map((m) => <SelectItem key={m.id} value={m.id}>{m.first_name} {m.last_name}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Start date"><Input type="date" value={f.start_date} onChange={(e) => set("start_date", e.target.value)} /></Field>
        <Field label="Probation period (months)"><Input type="number" min="0" value={f.probation_months} onChange={(e) => set("probation_months", e.target.value === "" ? "" : Number(e.target.value))} data-testid="probation-months-input" /></Field>
        <Field label="End date (optional)"><Input type="date" value={f.end_date} onChange={(e) => set("end_date", e.target.value)} /></Field>
        <Field label="Date of birth (for birthday leave)"><DateField value={f.date_of_birth} onChange={(v) => set("date_of_birth", v)} testid="edit-emp-dob" /></Field>
        <Field label="Holiday region">
          <Select value={f.region} onValueChange={(v) => set("region", v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{REGIONS.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Bank-holiday treatment">
          <Select value={f.bank_holiday_treatment} onValueChange={(v) => set("bank_holiday_treatment", v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="additional">Additional to annual leave</SelectItem>
              <SelectItem value="included">Included in entitlement</SelectItem>
              <SelectItem value="excluded">Excluded from tracker</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Status">
          <Select value={f.active ? "active" : "inactive"} onValueChange={(v) => set("active", v === "active")}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem></SelectContent>
          </Select>
        </Field>
      </div>
      <div className="mt-4 pt-4 border-t border-slate-100">
        <h4 className="text-sm font-semibold text-[#0B1A2F] mb-3">Personal & HR details</h4>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Preferred name"><Input value={f.preferred_name} onChange={(e) => set("preferred_name", e.target.value)} data-testid="edit-emp-preferred-name" /></Field>
          <Field label="Phone"><Input value={f.phone} onChange={(e) => set("phone", e.target.value)} data-testid="edit-emp-phone" /></Field>
          <Field label="Direct dial (work)"><Input value={f.work_phone} onChange={(e) => set("work_phone", e.target.value)} placeholder="e.g. +44 20 7946 0000" data-testid="edit-emp-work-phone" /></Field>
          <Field label="Address"><Input value={f.address} onChange={(e) => set("address", e.target.value)} /></Field>
          <Field label="NI number"><Input value={f.ni_number} onChange={(e) => set("ni_number", e.target.value)} /></Field>
          <Field label="Emergency contact"><Input value={f.emergency_contact} onChange={(e) => set("emergency_contact", e.target.value)} data-testid="edit-emp-emergency-contact" /></Field>
          <Field label="Emergency relationship"><Input value={f.emergency_relationship} onChange={(e) => set("emergency_relationship", e.target.value)} /></Field>
          <Field label="Right to work"><Input value={f.right_to_work} onChange={(e) => set("right_to_work", e.target.value)} placeholder="e.g. UK passport verified" /></Field>
          <Field label="Employment type">
            <Select value={f.employment_type} onValueChange={(v) => set("employment_type", v)}>
              <SelectTrigger data-testid="edit-emp-employment-type"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="full_time">Full time</SelectItem><SelectItem value="part_time">Part time</SelectItem><SelectItem value="fixed_term">Fixed term</SelectItem><SelectItem value="contract">Contract</SelectItem></SelectContent>
            </Select>
          </Field>
          <Field label="Notice period"><Input value={f.notice_period} onChange={(e) => set("notice_period", e.target.value)} placeholder="e.g. 1 month" /></Field>
          <Field label="Benefits"><Input value={f.benefits} onChange={(e) => set("benefits", e.target.value)} placeholder="e.g. Pension, private health" data-testid="edit-emp-benefits" /></Field>
          <Field label="Equipment issued"><Input value={f.equipment} onChange={(e) => set("equipment", e.target.value)} placeholder="e.g. Laptop, phone, pass" data-testid="edit-emp-equipment" /></Field>
          <Field label="Medical / accessibility notes"><Input value={f.medical_notes} onChange={(e) => set("medical_notes", e.target.value)} /></Field>
        </div>
      </div>
      <DialogFooter>
        <Button data-testid="edit-emp-save" onClick={save} className="bg-[#2EA3FD] hover:bg-[#1B8EE6]">Save changes</Button>
      </DialogFooter>
    </DialogContent>
  );
}
